import { MapTheme } from "@biggeo/bg-server-lib/studio";
import {
    Grid,
    ImageWithCheckbox,
    Stack,
    Typography,
    UserAvatar,
} from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import capitalize from "lodash/capitalize";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import mapboxgl from "mapbox-gl";
import { MapContextDataset } from "../mapbox/context/types";
import { setCustomShapeStyling } from "../utils/style-utils";
import { getMapFeatures } from "../utils/geometry-utils";
import { getMapStyleImage, getMapStyleSprite } from "@biggeo/bg-utils";

export const DEFAULT_MAP_STYLE: {
    key: MapTheme;
    url: string;
    sprite?: string;
} = {
    key: MapTheme.light,
    url: "mapbox://styles/mapbox/light-v11?optimize=true",
    sprite: "mapbox://sprites/mapbox/light-v11",
};

export const getMapStyleFromSprite = (
    sprite?: string,
    onStyleChange?: (style: MapTheme) => void
): MapTheme => {
    if (!sprite) {
        onStyleChange?.(DEFAULT_MAP_STYLE.key);
        return DEFAULT_MAP_STYLE.key;
    }

    const styles = Object.values(MapTheme);
    const foundStyle =
        find(styles, (style) => sprite.includes(style)) ||
        DEFAULT_MAP_STYLE.key;

    onStyleChange?.(foundStyle);

    return foundStyle;
};

export interface IMapStyles {
    readonly isMapLoaded: boolean;
    readonly map: React.MutableRefObject<mapboxgl.Map | null>;
    readonly draw: React.MutableRefObject<MapboxDraw | null>;
    readonly onStyleChange?: (style: MapTheme) => void;
    readonly currentStyle?: mapboxgl.Style;
    readonly datasets: MapContextDataset[];
}

const MapStyles = ({
    isMapLoaded,
    map,
    onStyleChange,
    currentStyle,
    draw,
    datasets,
}: IMapStyles) => {
    const style = currentStyle
        ? getMapStyleFromSprite(currentStyle.sprite, onStyleChange)
        : DEFAULT_MAP_STYLE.key;

    const handleSelection = (s: MapTheme) => {
        onStyleChange?.(s);

        if (isMapLoaded && map.current) {
            const currentZoom = map.current.getZoom();

            if (!isEqual(style, s)) {
                map.current.setStyle(getMapStyleSprite(s));

                setTimeout(() => {
                    if (map.current) {
                        setCustomShapeStyling({
                            map: map.current,
                            isMapLoaded,
                            datasets,
                            geometries: getMapFeatures(draw, isMapLoaded),
                        });
                    }
                }, 200);

                // A barely perceptible zoom to trigger a refetch when multiple
                // datasets are toggled on and the user is switching styles.
                map.current.zoomTo(currentZoom - 0.05);
            }
        }
    };

    return (
        <Grid
            container
            spacing={4}
            sx={{
                padding: 4,
            }}
            justifyContent="center"
        >
            {pipe(
                Object.values(MapTheme),
                A.map((_style) => (
                    <Grid item xs={6} key={_style} minWidth={28.5}>
                        <Stack
                            gap={2}
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                        >
                            <ImageWithCheckbox<MapTheme>
                                id={_style}
                                selected={isEqual(style, _style)}
                                image={getMapStyleImage(_style)}
                                onClick={handleSelection}
                                invertColors={isEqual(_style, MapTheme.dark)}
                            />
                            <Typography variant="title3">
                                {capitalize(_style)}
                            </Typography>
                        </Stack>
                    </Grid>
                ))
            )}
        </Grid>
    );
};

export default MapStyles;
